/**
 * The Visual view's end of the results grid's "Add to WHERE": a WHERE
 * condition builder (`VqeFilterChipBuilder`, add mode) calls this with its edit
 * context and columns; when the grid has posted a cell for that document
 * (`cellFilterDraftStore`), the cell's column is found in the statement
 * (`resultColumnWhereField` → the builder's own `resolveByField`) and the
 * builder opens a draft pre-filled with Table Explorer's "Add to filter"
 * condition (`cellFilterCondition`) — so a lookup, enum or JSON column gets
 * its own editor, and applying writes exactly what the chip writes.
 *
 * Only the top-level statement's builder serves a request (a drilled or
 * set-operation branch context carries `statementInfo`), and only for the
 * statement the request names when it names one; the first one to take it
 * opens the draft. A column header's "Add to Filter" request opens the
 * column's own default draft (Table Explorer's header item: default operator,
 * no value).
 */

import { useEffect } from 'react';

import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { toast } from '../../../../components/ui/toaster';
import type { FilterGroup } from '../../core/types/workspaceTypes';
import { extractStructureForContext, statementSpanOf, type VqeEditContext } from '../../visual-query-editor/edit/editContext';
import type { ResolvedFilterColumn, VqeFilterColumns } from '../../visual-query-editor/render/common/useVqeFilterColumns';
import { useCellFilterDraftStore } from '../stores/cellFilterDraftStore';
import { cellFilterCondition } from '../utils/addToWhere';
import { resultColumnWhereField, type ResultColumnField } from '../utils/resultColumnWhereField';

export type AddToWhereFailure =
  | ResultColumnField['kind']
  | 'not-a-column'
  | 'not-editable'
  | 'not-opened'
  | 'unsupported-statement'
  | 'control-flow';

const FAILURE_TEXT: Record<Exclude<AddToWhereFailure, 'field'>, (columnName: string) => string> = {
  unfilterable: (c) => `WHERE can't filter “${c}”: it is computed by an aggregate or window function.`,
  unknown: (c) => `Can't tell which table “${c}” comes from in this statement.`,
  'not-a-column': (c) =>
    `“${c}” is not a table column of this statement, so the Visual view has no condition for it. Use the SQL view to filter it.`,
  'not-editable': () => "The Visual view can't add a condition to this statement. Use the SQL view.",
  'not-opened': () => 'The Visual Query Editor did not open a condition for this statement.',
  'unsupported-statement': () => 'Add to WHERE adds a condition to a SELECT, UPDATE or DELETE statement.',
  'control-flow': () =>
    "Add to WHERE can't edit a SELECT inside IF, WHILE or a BEGIN … END block. Highlight the SELECT and use Add to WHERE again.",
};

/** Tell the user why "Add to WHERE" added nothing. */
export function notifyAddToWhereFailed(columnName: string, reason: Exclude<AddToWhereFailure, 'field'>): void {
  const description = FAILURE_TEXT[reason](columnName);
  toast.default({ title: 'Not added to WHERE', description });
}

export function useCellFilterDraftRequest(
  editContext: VqeEditContext | null,
  cols: VqeFilterColumns,
  openDraft: (resolved: ResolvedFilterColumn) => void,
): void {
  const { track } = useAnalytics();
  const documentId = editContext && !editContext.statementInfo ? editContext.documentId : null;
  const request = useCellFilterDraftStore((s) => (documentId && s.request?.documentId === documentId ? s.request : null));
  const ready = !cols.picker.isLoading;
  const { resolveByField, engine } = cols;

  useEffect(() => {
    if (!request || !editContext || !ready) return;
    const span = statementSpanOf(editContext);
    if (request.statementStart !== undefined && span?.start !== request.statementStart) return;
    if (!useCellFilterDraftStore.getState().take(request)) return;
    let field: ResultColumnField = { kind: 'unknown' };
    try {
      if (span) {
        field = resultColumnWhereField(
          extractStructureForContext(editContext),
          span.options,
          request.column,
          editContext.service.getSchemaSnapshot(),
        );
      }
    } catch {
      // Unreadable statement — reported as unknown below.
    }
    if (field.kind !== 'field') {
      notifyAddToWhereFailed(request.column.name, field.kind);
      return;
    }
    const resolved = resolveByField(field.sql);
    if (!resolved) {
      notifyAddToWhereFailed(request.column.name, 'not-a-column');
      return;
    }
    if (request.columnOnly) {
      openDraft(resolved);
      track('feature:qe_add_to_filter', { engine, view: request.columnOnly.view });
      return;
    }
    const condition = cellFilterCondition(request.value, resolved.column);
    const draft: FilterGroup = { field: resolved.draft.field, conditions: [condition] };
    openDraft({ ...resolved, draft });
    track('feature:qe_add_to_where', { engine, view: 'visual', operator: condition.operator });
  }, [request, editContext, ready, resolveByField, engine, openDraft, track]);
}
